import { useQuery, UseQueryOptions } from "@tanstack/react-query"
import { useMutation, UseMutationOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { API_DEFAULT } from "src/constants/minutApi"
import { portfolioAnalyticsKeys } from "src/data/portfolioAnalytics/portfolioAnalyticsQueryCache"
import {
  TDeviceStatusByHomegroupId,
  TDeviceStatusByOrganization,
  TWifiHealthAnalyticsByOrganization,
} from "src/data/portfolioAnalytics/types/portfolioAnalyticsTypes"
import { useMinutQuery } from "src/hooks/minutQuery"
import { minutApiHttpClient } from "src/utils/minutApiHttpClient"

function portfolioAnalyticsApiPath(orgId: string) {
  return `${API_DEFAULT}/organizations/${orgId}/analytics/aggregated_group`
}

export function useFetchDeviceStatusByOrganization({
  orgId,
  options,
}: {
  orgId: string
  options?: UseQueryOptions<
    TDeviceStatusByOrganization,
    AxiosError,
    TDeviceStatusByOrganization,
    ReturnType<typeof portfolioAnalyticsKeys.deviceStatusByOrganization>
  >
}) {
  async function fetchDeviceStatusByOrganization() {
    const apiPath = portfolioAnalyticsApiPath(orgId)

    const response = await minutApiHttpClient.get<TDeviceStatusByOrganization>(
      `${apiPath}/device_status`
    )
    return response.data
  }

  return useQuery({
    queryKey: portfolioAnalyticsKeys.deviceStatusByOrganization({
      orgId,
    }),
    queryFn: fetchDeviceStatusByOrganization,
    ...options,
  })
}

export function useFetchWifiHealthByOrganization({
  orgId,
  options,
}: {
  orgId: string
  options?: UseQueryOptions<
    TWifiHealthAnalyticsByOrganization,
    AxiosError,
    TWifiHealthAnalyticsByOrganization,
    ReturnType<typeof portfolioAnalyticsKeys.wifiHealthByOrganization>
  >
}) {
  async function fetchWifiHealthByOrganization() {
    const apiPath = portfolioAnalyticsApiPath(orgId)

    const response =
      await minutApiHttpClient.get<TWifiHealthAnalyticsByOrganization>(
        `${apiPath}/wifi_quality_by_homegroup`
      )
    return response.data
  }

  return useQuery({
    queryKey: portfolioAnalyticsKeys.wifiHealthByOrganization({
      orgId,
    }),
    queryFn: fetchWifiHealthByOrganization,
    ...options,
  })
}

export function useDeviceStatusDrilldownByHomegroupId({
  orgId,
  options,
}: {
  orgId: string
  options?: UseMutationOptions<
    TDeviceStatusByHomegroupId,
    AxiosError,
    string,
    unknown
  >
}) {
  async function fetchDeviceStatusByHomegroupId(homegroupId: string) {
    const apiPath = portfolioAnalyticsApiPath(orgId)

    const response = await minutApiHttpClient.get<TDeviceStatusByHomegroupId>(
      `${apiPath}/device_status/${homegroupId}`
    )
    return response.data
  }

  return useMutation({
    mutationFn: fetchDeviceStatusByHomegroupId,
    ...options,
  })
}

export function useFetchIntegrationsCoveragePerIntegration({
  orgId,
}: {
  orgId: string
}) {
  const query =
    useMinutQuery<"/organizations/{organization_id}/analytics/aggregated_group/integrations/coverage_per_integration">(
      {
        queryKey: portfolioAnalyticsKeys.integrationsCoveragePerIntegration({
          orgId,
        }),
        queryPath: `/organizations/${orgId}/analytics/aggregated_group/integrations/coverage_per_integration`,
      }
    )

  return query
}
